import { ButtonHTMLAttributes, ReactNode, forwardRef } from "react";
import { focusRing } from "@ui/tokens/interaction";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "disabled"
  | "soft-add"
  | "ghost"
  | "create"
  | "danger";

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  children: ReactNode;
  variant?: ButtonVariant;
  icon?: ReactNode;
  type?: "button" | "submit";
  className?: string;
}

/**
 * 44px hit area without changing the visible height: the handoff draws a 40px button and the
 * touch rule asks for 44. A pseudo-element satisfies both, as it does for the pills.
 */
const touchTarget =
  "relative after:absolute after:inset-x-0 after:top-1/2 after:h-touch after:-translate-y-1/2 after:content-['']";

const baseStyles =
  `inline-flex items-center justify-center gap-2 font-ui whitespace-nowrap transition duration-hover ${touchTarget}`;

const variantStyles: Record<ButtonVariant, string> = {
  primary: `rounded-field app:rounded-pill px-4.5 app:px-8 py-3.5 [font-size:var(--button-primary-size)] [line-height:1.2] [font-weight:var(--button-primary-weight)] [letter-spacing:var(--button-primary-tracking)] bg-primary-fill bg-primary-glow text-on-primary-fill shadow-primary hover:bg-primary-fill-raised hover:bg-primary-glow-hover active:bg-primary-glow-hover active:translate-y-px active:shadow-primary-pressed app:shadow-button app:hover:-translate-y-px app:hover:shadow-button-hover app:active:translate-y-px app:active:shadow-button-pressed modal:rounded-pill modal:px-8 modal:bg-ink modal:text-[color:var(--modal-primary-ink,rgb(var(--surface-cream)))] modal:hover:bg-ink-raised modal:shadow-button-modal modal:hover:shadow-button-modal-hover ${focusRing}`,
  secondary: `rounded-field app:rounded-pill px-4.5 app:px-7 py-3 text-control bg-surface-raised text-ink-strong border border-line-border hover:bg-row-hover app:bg-tint/[0.14] app:border-transparent app:text-ink app:shadow-secondary-soft app:hover:bg-tint/[0.24] modal:bg-ink/[0.08] modal:border-transparent modal:text-ink modal:hover:bg-ink/[0.14] active:translate-y-px ${focusRing}`,
  disabled: "rounded-field app:rounded-pill px-4.5 app:px-7 py-3 text-control bg-[var(--disabled-surface,rgb(var(--disabled-bg)))] text-[color:var(--disabled-ink,rgb(var(--disabled-fg)))] [opacity:var(--disabled-opacity)] cursor-not-allowed",
  "soft-add": `rounded-pill px-4 py-2.25 text-control-sm bg-transparent text-ink-body border-1.5 border-dashed border-line-dashed hover:bg-row-hover hover:border-line-border active:translate-y-px ${focusRing}`,
  ghost: `rounded-pill px-3 py-1.75 text-control-sm bg-transparent text-ink-meta hover:bg-row-hover hover:text-ink-body active:translate-y-px ${focusRing}`,
  create: `rounded-pill h-control px-5 text-control-lg [font-weight:var(--button-primary-weight)] bg-create bg-primary-glow text-on-create shadow-pink-edge hover:bg-create-raised hover:bg-primary-glow-hover active:bg-primary-glow-hover hover:shadow-pink-edge-hover active:shadow-pink-edge-pressed ${focusRing}`,
  /**
   * Outlined, not filled: archiving is reversible and keeps the resources. A solid red would
   * read as deletion, and the system already reserves solid fills for the primary action.
   */
  danger: `rounded-field app:rounded-pill px-4.5 app:px-7 py-3 text-control bg-surface-raised text-danger border border-danger-line hover:bg-danger-bg app:bg-danger app:text-white app:border-transparent app:shadow-danger-soft app:hover:bg-danger-hover modal:bg-danger-modal modal:text-white modal:border-transparent modal:hover:bg-danger-modal-hover modal:shadow-danger-modal-ring active:translate-y-px ${focusRing}`,
};

/**
 * The ref is forwarded so a headless primitive can *be* this button rather than copy its classes —
 * the "Ajouter" menu anchors on the create variant, and one styling source is the whole point.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { children, variant = "primary", icon, type = "button", className = "", disabled, ...props },
  ref,
) {
  const isDisabled = disabled || variant === "disabled";
  const appliedVariant = isDisabled ? "disabled" : variant;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      className={`${baseStyles} ${variantStyles[appliedVariant]} ${className}`}
      {...props}
    >
      {icon}
      {children}
    </button>
  );
});